/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useMemo, useState, isValidElement } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MRT_ColumnDef, MRT_SortingState } from 'material-react-table';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

import {
  formatDate,
  getProcessData,
  getStoredCustomerDetails,
  NUMBER_OF_ITEMS_PER_PAGE,
} from '../../../../Shared/Utilities/utils';
import {
  JobStatuses,
  DATA_STATUS,
  SortOrderType,
  FilterItem,
  DateFormat,
  states,
  NumberOfRowsOptions,
  SnackbarSeverity,
  USER_PROCESSES,
} from '../../../../Shared/Constants/App';
import { StyleConstants } from '../../../../Shared/Constants/Style.Constants';

import WorkOrdersApiService from '../../Services/WorkOrdersService';
import JobViewApiService from '../../Services/JobViewService';
import JobCreateApiService from '../../Services/JobCreateService';
import { IJobRow, TabPanelProps } from '../../Models/JobsView.Model';
import { JobType, WorkOrderStatus, JobStatus, BidDraftStep } from '../../../../API';

import DataGrid from '../../../../Shared/Components/Common/DataGrid/DataGrid';
import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';
import { TaskData } from '../WorkTasks/WorkTasks';
import { IJobOverview } from '../JobsListView/JobsListView';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import { IBidList } from '../JobDetails/OneTimeJobDetails';
import { IProfileCardProps } from '../../../Customer/Components/ViewFacilityDetails/ViewFacilityDetails';
import DeleteJobs from '../DeleteJobDialog/DeleteJobDialog';
import { Labels } from '../../../../Shared/Components/Common/GraphContainer/GraphContainer';

import theme from '../../../../Shared/Themes/theme';
import { Icons } from '../../../../Shared/Constants/Icons';
import {
  ActionsColumn,
  JobLink,
  Linking,
  Menus,
  MenuWrapper,
} from '../../../../Shared/Components/Common/DataGrid/DataGrid.styles';
import { CustomerContainer } from '../../../../Shared/Components/Layout/styles';
import { SnackBarWrapper, TabSection, TabTitle } from '../JobDetailsOverView/JobDetailsOverview.styles';
import Button, { ButtonType } from '../../../../Shared/Components/Common/Buttons/Button';
import { TabsWrapper } from './JobsView.styles';
import { StyledPagination } from '../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import { DropDown, SelectInput } from '../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';
import { Options } from '../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import WhiteTooltip from '../../../../Shared/Components/Common/CommonStyle/Tooltip.Style';

export interface IJobs {
  facilityName: string;
  label: string;
  noOfService: number;
  status: string;
  startDate: string;
  stopDate?: string;
  jobType: string;
  facilityType: string;
  location: string;
  createdOn: string;
  area: string;
  duration: string;
  volume: string;
  percentageMatch: number;
  noOfDaysLeft: number;
  bidStatus: string;
  bidEstimate: number;
  lastActivity: string;
  frequency: string;
  serviceCategory: any;
  facilityStatus: string;
  areaSpecification: string;
  numOfHours: number;
  ratePerHour: number;
  costPerService: number;
  servicesPerWeek: number;
  weeklyCost: number;
  profitPerWeek: number;
  weeklyBid: number;
  numOfWeeks: number;
  totalServices: number;
  totalJobEstimation: number;
  jobStatus: string;
  bidCount: number;
  totalTasks: number;
  approvedByCustomer: number;
  sortingFn?: any;
  facility?: any;
  address?: any;
  jobId?: string;
  executionType?: string;
  customerId?: string;
  dataStatus?: string;
  parentWorkOrderId?: string;
  woScheduleMasterId?: string;
  workOrderScheduleId?: string;
  isRescheduled?: boolean;
  scheduleDate?: string;
  complete?: number;
}

export interface IJobVendorDetails {
  mdVendorType: string;
  vendorId: string;
}

export interface IBidSummaryResponse {
  bidVersion: number;
  profitMargin: number;
  isFlatMargin: string;
  vendorId: string;
}

export interface IJobBids {
  bidId: string;
  dataStatus: string;
  draftStep: BidDraftStep;
}

export interface IAddressDetails {}

export interface IJobServices {}
export interface IJobDetails {
  jobServices: IJobServices[];
  profitMargin: number;
  isFlatMargin: string;
  isResubmittingBid: boolean;
  jobStatus: string;
  jobBids: IJobBids[];
}

export interface ModifyQuoteDetails {
  jobId: string;
  jobName: string;
  jobType: string;
  customerId: string;
  customerEmail: string;
  customerName: string;
  isCustomerApprovalAvailable: boolean;
  subJobId: string;
  subJobBidId: string;
  jobCustodian: string;
  startDate: string;
  stopDate: string;
  address: IAddressDetails;
  jobVendors: IJobVendorDetails[];
  jobBids: IJobBids[];
  jobServices: IJobServices[];
  jobDetails: IJobDetails;
  bidSummaryResponse: IBidSummaryResponse;
}

export enum DataStatus {
  Active = 'A',
  Cancelled = 'X',
  Closed = 'C',
  Draft = 'D',
}

export enum StatusType {
  Awarded = 'Awarded',
  ClosedForBids = 'Closed for bids',
  Ongoing = 'Ongoing',
  OpenForBids = 'Open for bids',
}

enum JobStatusCheck {
  Status = 'completed-jobs',
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function TabPanel(props: TabPanelProps & { style?: React.CSSProperties }) {
  const { children, value, index, style, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={style}
      {...other}>
      {value === index && children}
    </div>
  );
}

const JobsView = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const { t } = useTranslation(['Dashboard', 'tableHeader', 'homePage', 'altTexts', 'calendar']);
  const [recurringJob, setRecurringJob] = useState<IJobs[]>([]);
  const [oneTimeJobs, setOneTimeJobs] = useState<IJobs[]>([]);
  const [completedJobs, setCompletedJobs] = useState<IJobs[]>([]);
  const location = useLocation();
  const [tabSelected, setTabSelected] = useState(
    location?.pathname.includes('onetime') ? 1 : location?.pathname.includes(JobStatusCheck.Status) ? 2 : 0
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rowInfo, setRowInfo] = useState<any>({});
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const fromDate = '2023-01-01';

  const [sortBy, setSortBy] = useState('jobName');
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [newSort, setNewSort] = useState<boolean>();
  const [newSortOrder, setNewSortOrder] = useState('');
  const [dialogueBoxOpen, setDialogueBoxOpen] = useState(false);
  const [deleteJobId, setDeleteJobId] = useState('');
  const [deleteJobName, setDeleteJobName] = useState('');
  const [clickedRow, setClickedRow] = useState(null);
  const [customerId, setCustomerId] = useState<string>();
  const [jobType, setJobType] = useState('');
  const [columnFilters, setColumnFilters] = useState<FilterItem[]>([]);
  const [isValueOpen, setValueOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loadingBidJobId, setLoadingBidJobId] = useState<string | null>(null);
  const [loadingViewPriceDetailsJobId, setLoadingViewPriceDetailsJobId] = useState<string | null>(null);

  const noDataYet = t('noData:noDataYet');
  const noDataComeBack = t('noData:comeBackLater');
  const navigate = useNavigate();
  const toggleValue = () => {
    setValueOpen(!isValueOpen);
  };

  const handleClickOpen = (): void => {
    setDialogueBoxOpen(true);
  };

  const fetchCompletedJobs = async (
    pageNumber?: number,
    sortBy?: any,
    sortOrder?: any,
    columnFilters: FilterItem[] = []
  ) => {
    setIsLoading(true);
    let sortOrderString = sortOrder;
    if (sortOrder) {
      sortOrderString = SortOrderType.DESC;
    } else {
      if (sortBy) sortOrderString = SortOrderType.ASC;
    }
    const searchText = columnFilters.find((filter) => filter.id === 'name')?.value;

    const { customerId } = getStoredCustomerDetails();
    try {
      const allCompletedJobsResponse = await WorkOrdersApiService.getAllWorkOrders({
        customerId,
        status: WorkOrderStatus.Completed,
        pageNumber,
        sortBy,
        sortOrder: sortOrderString,
        searchText,
      });
      if (allCompletedJobsResponse?.data?.length === 0) {
        setCompletedJobs([]);
      }
      if (
        allCompletedJobsResponse.data &&
        !isEmpty(allCompletedJobsResponse.data) &&
        isEmpty(allCompletedJobsResponse.errors)
      ) {
        setTotalCount(allCompletedJobsResponse.metadata.totalCount);
        const updatedData = await Promise.all(
          allCompletedJobsResponse?.data.map((job: any) => {
            return {
              ...JSON.parse(job.jobBidSummary.jobDetails),
              ...job,
              facilityName: job?.facilities[0]?.facilityName ?? job?.facilities[0]?.facilityAddress,
              numberOfServices: job.services.length,
            };
          })
        );
        setCompletedJobs(updatedData);
      } else if (!isEmpty(allCompletedJobsResponse.errors) && allCompletedJobsResponse?.errors[0]?.message) {
        setSnackbarMessage(allCompletedJobsResponse?.errors[0]?.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.log('error occurred while fetching completed jobs data', error);
      setSnackbarMessage(error as string);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setIsLoading(false);
  };

  const fetchJobsView = useCallback(
    async (
      jobType: string,
      currentPage: number,
      limit: number,
      sortBy?: any,
      sortOrder?: any,
      columnFilters: FilterItem[] = []
    ) => {
      try {
        setIsLoading(true);
        setJobType(jobType);
        const { customerId = '', userId } = getStoredCustomerDetails() || {};
        setCustomerId(customerId);
        let sortOrderString = sortOrder;
        if (sortOrder) {
          sortOrderString = SortOrderType.DESC;
        } else {
          if (sortBy) sortOrderString = SortOrderType.ASC;
        }

        const allJobResponse = await JobViewApiService.jobView(
          customerId,
          userId,
          fromDate,
          jobType,
          currentPage,
          limit,
          sortBy,
          sortOrderString,
          columnFilters
        );
        const allJobsData: any[] = [];
        const promises: Promise<any>[] = [];
        if (allJobResponse?.data?.data?.length) {
          allJobResponse.data.data.forEach((job: any, index: number) => {
            const isOpenToBid = job?.status === JobStatus.OpenToBid;
            const bidCountPromise = isOpenToBid
              ? JobCreateApiService.getAllJobBids(job?.jobId).then((r) => r?.data?.length ?? 0)
              : Promise.resolve(0);

            const promise = Promise.all([
              JobCreateApiService.jobSummaryV2(customerId, job?.jobId, false),
              bidCountPromise,
            ]).then(([jobSummaryResponse, bidCount]) => {
              allJobsData[index] = {
                ...job,
                ...jobSummaryResponse.data,
                status: jobSummaryResponse.data?.dataStatus === 'Draft' ? 'Draft' : job.status,
                bidCount,
              };
              return undefined;
            });

            promises.push(promise);
          });
        }

        await Promise.all(promises);
        setIsLoading(false);
        if (jobType === 'OneTime') {
          setOneTimeJobs(allJobsData);
          setTotalCount(allJobResponse?.data?.metadata?.totalCount);
        } else {
          setRecurringJob(allJobsData ?? []);
          setTotalCount(allJobResponse?.data?.metadata?.totalCount);
        }

        return [];
      } catch (e) {
        console.error('Error', e);
        return [];
      }
    },
    []
  );

  const sortingJobs = (sortingObj: any) => {
    setSorting(sortingObj);
    if (sortingObj.length) {
      setNewSortOrder(sortingObj[0].id);
      setNewSort(sortingObj[0].desc);
      if (tabSelected === 2) {
        fetchCompletedJobs(currentPage, sortingObj[0].id, sortingObj[0].desc, columnFilters);
      } else {
        fetchJobsView(
          tabSelected === 1 ? JobType.OneTime : JobType.Recurring,
          currentPage,
          itemsPerPage,
          sortingObj[0].id,
          sortingObj[0].desc
        );
      }
    } else {
      if (tabSelected === 2) {
        fetchCompletedJobs(currentPage);
      } else {
        fetchJobsView(tabSelected === 1 ? JobType.OneTime : JobType.Recurring, currentPage, itemsPerPage);
      }
    }
  };

  const handleChange = (event: any, value: number): void => {
    setCurrentPage(value);
    fetchCompletedJobs(value, newSortOrder, newSort, columnFilters);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newItemsPerPage = Number(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchJobsView(
      tabSelected === 1 ? JobType.OneTime : JobType.Recurring,
      currentPage,
      newItemsPerPage,
      newSortOrder,
      newSort
    );
  };

  const filterOneTimeJobs = (filtersObj: any): void => {
    setColumnFilters(filtersObj);
    if (filtersObj?.length) {
      fetchJobsView(
        tabSelected === 1 ? JobType.OneTime : JobType.Recurring,
        currentPage,
        itemsPerPage,
        newSortOrder,
        newSort,
        filtersObj
      );
    } else {
      fetchJobsView(
        tabSelected === 1 ? JobType.OneTime : JobType.Recurring,
        currentPage,
        itemsPerPage,
        newSortOrder,
        newSort,
        []
      );
    }
  };

  const filterJobs = (filtersObj: any): void => {
    setColumnFilters(filtersObj);
    if (filtersObj?.length) {
      fetchCompletedJobs(currentPage, newSortOrder, newSort, filtersObj);
    } else {
      fetchCompletedJobs(currentPage, newSortOrder, newSort, []);
    }
  };

  const handleChangeTabs = useCallback(
    (event: React.SyntheticEvent, newValue: number, sortBy?: any, sortOrder?: any) => {
      setTabSelected(newValue);
      window.history.pushState(
        null,
        'Peazy',
        `/jobs-view/${newValue === 0 ? 'recurring' : newValue === 1 ? 'onetime' : JobStatusCheck.Status}`
      );
      if (newValue === 1) {
        setItemsPerPage(NUMBER_OF_ITEMS_PER_PAGE);
        fetchJobsView('OneTime', currentPage, itemsPerPage, sortBy, sortOrder);
      } else if (newValue === 2) {
        fetchCompletedJobs(currentPage, sortBy, sortOrder, columnFilters);
      } else {
        setItemsPerPage(NUMBER_OF_ITEMS_PER_PAGE);
        fetchJobsView('Recurring', currentPage, itemsPerPage, sortBy, sortOrder);
      }
    },
    []
  );

  const myCustomSortingFn = () => {
    setSortBy(SortOrderType.DESC);
  };

  useEffect(() => {
    if (location?.state?.jobName) {
      setSnackbarMessage(`${location?.state?.jobName} Job successfully created.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }
    setCurrentPage(1);
    setTotalCount(0);
    if (tabSelected === 1) {
      fetchJobsView('OneTime', currentPage, itemsPerPage);
    } else if (tabSelected === 2) {
      fetchCompletedJobs(currentPage);
    } else {
      fetchJobsView('Recurring', currentPage, itemsPerPage);
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setTotalCount(0);
  }, [tabSelected]);

  useEffect(() => {
    setTabSelected(
      location?.pathname.includes('onetime') ? 1 : location?.pathname.includes(JobStatusCheck.Status) ? 2 : 0
    );
  }, [location.pathname]);

  // material UI Logic
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderJobLinkCell = (renderedCellValue: string | React.ReactNode | null | undefined, row: any) => {
    const { jobId } = row.original as unknown as IJobRow;
    const jobType = row.original.jobType;

    if (typeof renderedCellValue === 'string') {
      const jobs = jobType === 'OneTime' || jobType === 'Emergency' ? oneTimeJobs : recurringJob;

      return (
        <JobLink
          to={{ pathname: `/onetimejobdetails`, search: `?id=${jobId}` }}
          onClick={() =>
            localStorage.setItem(
              'locationState',
              JSON.stringify({
                facilityItem: {
                  ...jobs[row.index],
                  estimationType: 'estimateWithinPeazy',
                  executionType: 'self',
                },
                jobStatus: jobs[row.index].status,
                facility: {
                  ...jobs[row.index].facility,
                  address: jobs[row.index].address,
                  facilityType: jobs[row.index].facilityType,
                },
                job: { ...jobs[row.index], customerId },
              })
            )
          }
          state={{
            facilityItem: { ...jobs[row.index], estimationType: 'estimateWithinPeazy', executionType: 'self' },
            jobStatus: jobs[row.index]?.status,
            facility: {
              ...jobs[row.index]?.facility,
              address: jobs[row.index]?.address,
              facilityType: jobs[row.index]?.facilityType,
            },
            job: { ...jobs[row.index], customerId },
          }}>
          {renderedCellValue}
        </JobLink>
      );
    }

    return '-';
  };

  const renderCell = (renderedCellValue: React.ReactNode) => {
    // If renderedCellValue is a string, return it. Otherwise, return a fallback '-'
    if (typeof renderedCellValue === 'string' || typeof renderedCellValue === 'number') {
      return renderedCellValue ? renderedCellValue : '-';
    }
    return '-';
  };

  const formatDateCell = (cell: any) => {
    const cellValue = cell.getValue();
    if (cellValue) {
      return formatDate(cellValue, DateFormat.StandardDate);
    }
    return '-';
  };

  const renderStatusCell = (renderedCellValue: string | React.ReactNode | undefined) => {
    const selectedColor = theme.palette.text.secondary;

    if (typeof renderedCellValue === 'string') {
      return <span style={{ color: selectedColor }}>{DATA_STATUS[renderedCellValue as keyof typeof DATA_STATUS]}</span>;
    }

    // If renderedCellValue is a ReactNode, return it
    if (isValidElement(renderedCellValue)) {
      return renderedCellValue;
    }

    return '-';
  };

  const fetchJobBids = async (jobId: string) => {
    try {
      const bidResponse = await JobCreateApiService.getAllJobBids(jobId);
      return bidResponse?.data || [];
    } catch (error) {
      console.error('Error fetching job bids:', error);
      setSnackbarMessage(t('noData:failedToLoadBidDetails'));
      setSnackbarSeverity(SnackbarSeverity.error);
      setSnackbarOpen(true);
      throw error;
    }
  };

  const handleViewPriceDetails = async (rowData: any) => {
    try {
      const jobId = rowData?.jobId;
      setLoadingViewPriceDetailsJobId(jobId);
      handleClose();
      const bids = await fetchJobBids(jobId);

      if (bids?.length > 0) {
        const bidData = bids[0];
        navigate(`/create-bid`, {
          state: {
            jobDetails: {
              ...rowData,
              jobEstimations: bidData?.jobEstimations || [],
              bidId: bidData?.vendorBidId || bidData?.bidId,
              vendorName: bidData?.vendorName || '',
              vendorId: bidData?.vendorId || '',
              serviceSchedule: JobType.OneTime,
            },
            finalQuote: bidData?.totalCost,
            oldTotalCost: bidData?.oldTotalCost,
            jobId: rowData?.jobId,
            customerId,
            isJobCreation: false,
            jobStatus: rowData.status,
          },
        });
      } else {
        setSnackbarMessage(t('noData:noBidDetailsFound'));
        setSnackbarSeverity(SnackbarSeverity.error);
        setSnackbarOpen(true);
      }
    } catch (error) {
      // Error already handled in fetchJobBids
    } finally {
      setLoadingViewPriceDetailsJobId(null);
    }
  };

  const handleBidIconClick = async (jobId: string, jobData: any) => {
    setLoadingBidJobId(jobId);

    try {
      const bids = await fetchJobBids(jobId);

      if (bids?.length > 0) {
        navigate(`/onetimejobdetails/bids?id=${jobId}`, {
          state: {
            job: jobData,
          },
        });
      } else {
        setSnackbarMessage(t('noData:noBidsFound'));
        setSnackbarSeverity(SnackbarSeverity.error);
        setSnackbarOpen(true);
      }
    } catch (error) {
      // Error already handled in fetchJobBids
    } finally {
      setLoadingBidJobId(null);
    }
  };

  const renderActionsCell = (row: any) => {
    const { jobType, status, jobId } = row.original as unknown as IJobRow;
    const bidCount: number = row?.original?.bidCount ?? 0;
    const isOpenForBids = status === JobStatus.OpenToBid;
    const showViewPriceDetails = status === JobStatuses.Created || status === JobStatus.Awarded;
    const isBidLoading = loadingBidJobId === jobId;
    const isViewPriceDetailsLoading = loadingViewPriceDetailsJobId === jobId;
    const bidTooltipTitle = bidCount > 0 ? `${t('homePage:viewBids')} (${bidCount})` : t('noData:noBidsYet');

    return (
      <ActionsColumn>
        {isOpenForBids && (
          <WhiteTooltip title={bidTooltipTitle}>
            <span>
              <IconButton
                onClick={async () => {
                  await handleBidIconClick(jobId, row?.original);
                }}
                size="large"
                disabled={isBidLoading || bidCount === 0}
                sx={{
                  '&:hover': {
                    background: bidCount > 0 ? theme.palette.primary.main : 'unset',
                  },
                  opacity: isBidLoading || bidCount === 0 ? 0.5 : 1,
                }}>
                {isBidLoading ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
                ) : (
                  <img src={Icons.BidIcon} alt={t('homePage:viewBids')} />
                )}
              </IconButton>
            </span>
          </WhiteTooltip>
        )}
        <IconButton
          onClick={(e) => {
            if (!isViewPriceDetailsLoading) {
              handleClick(e);
              setClickedRow(row.index);
              setRowInfo(row.original);
            }
          }}
          key={row.index}
          disabled={isViewPriceDetailsLoading}
          sx={{
            backgroundColor: clickedRow === row.index && open ? theme.palette.primary.main : 'unset',
            '&:hover': {
              background: isViewPriceDetailsLoading ? 'unset' : theme.palette.primary.main,
            },
            opacity: isViewPriceDetailsLoading ? 0.7 : 1,
          }}
          size="large"
          aria-haspopup="true"
          {...(open && { 'aria-expanded': 'true', 'aria-controls': 'account-menu' })}>
          {isViewPriceDetailsLoading ? (
            <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
          ) : (
            <MoreVertIcon sx={{ color: theme.palette.common.black }} />
          )}
        </IconButton>
        {clickedRow === row?.index && !isViewPriceDetailsLoading && (
          <MenuWrapper anchorEl={anchorEl} id="account-menu" open={open} onClose={handleClose} onClick={handleClose}>
            <Linking
              to={{ pathname: `/onetimejobdetails`, search: `?id=${rowInfo.jobId}` }}
              state={{
                facilityItem: { ...rowInfo, estimationType: 'estimateWithinPeazy', executionType: 'self' },
                jobStatus: rowInfo.status,
                facility: {
                  ...rowInfo.facility,
                  address: rowInfo.address,
                  facilityType: rowInfo.facilityType,
                },
                job: { ...rowInfo, customerId },
              }}
              onClick={() =>
                localStorage.setItem(
                  'locationState',
                  JSON.stringify({
                    facilityItem: { ...rowInfo, estimationType: 'estimateWithinPeazy', executionType: 'self' },
                    jobStatus: rowInfo.status,
                    facility: {
                      ...rowInfo.facility,
                      address: rowInfo.address,
                      facilityType: rowInfo.facilityType,
                    },
                    job: { ...rowInfo, customerId },
                  })
                )
              }>
              <Menus>
                <img src={Icons.EyeIcon} alt={t('altTexts:viewDetails')} />
                {t('dashboard:viewDetails')}
              </Menus>
            </Linking>
            {showViewPriceDetails && (
              <Menus
                onClick={() => handleViewPriceDetails(row?.original)}
                disabled={isViewPriceDetailsLoading}
                sx={{
                  opacity: isViewPriceDetailsLoading ? 0.5 : 1,
                }}>
                <img src={Icons.QuoteIcon} alt={t('altTexts:viewPriceDetails')} width={24} height={24} />
                {t('oneTimeJob:viewPriceDetails')}
              </Menus>
            )}
            {((jobType as JobType) === JobType.Emergency || (jobType as JobType) === JobType.OneTime) &&
              rowInfo.status === JobStatus.Draft && (
                <Linking
                  to="/createOneTimeJob"
                  state={{
                    jobRecord: { ...rowInfo, estimationType: 'estimateWithinPeazy', executionType: 'self' },
                    isJobCreation: false,
                    mediaFileIds: [],
                  }}>
                  <Menus>
                    <img src={Icons.EditBlackIcon} alt={t('altTexts:modify')} /> {t('dashboard:modify')}
                  </Menus>
                </Linking>
              )}
            {((jobType as JobType) === JobType.Emergency ||
              (jobType as JobType) === JobType.OneTime ||
              (jobType as JobType) === JobType.Recurring ||
              (jobType as JobType) === JobType.Periodic) &&
              rowInfo.status === JobStatus.OpenToBid && (
                <Linking
                  to={{ pathname: `/onetimejobdetails`, search: `?id=${rowInfo.jobId}` }}
                  state={{
                    facilityItem: { ...rowInfo, estimationType: 'estimateWithinPeazy', executionType: 'self' },
                    jobStatus: rowInfo.status,
                    facility: {
                      ...rowInfo.facility,
                      address: rowInfo.address,
                      facilityType: rowInfo.facilityType,
                    },
                    job: { ...rowInfo, customerId },
                    isModifyJobDetails: true,
                  }}
                  onClick={() => {
                    localStorage.setItem(
                      'locationState',
                      JSON.stringify({
                        facilityItem: { ...rowInfo, estimationType: 'estimateWithinPeazy', executionType: 'self' },
                        jobStatus: rowInfo.status,
                        facility: {
                          ...rowInfo.facility,
                          address: rowInfo.address,
                          facilityType: rowInfo.facilityType,
                        },
                        job: { ...rowInfo, customerId },
                        isModifyJobDetails: true,
                      })
                    );
                  }}>
                  <Menus>
                    <img src={Icons.EditBlackIcon} alt={t('altTexts:modify')} /> {t('dashboard:modify')}
                  </Menus>
                </Linking>
              )}
            {(rowInfo.status === JobStatuses.Draft ||
              rowInfo.status === JobStatuses.OpenToBid ||
              rowInfo.status === JobStatuses.PendingEstimation ||
              rowInfo.status === JobStatuses.PendingCustomerApproval ||
              rowInfo.status === JobStatuses.ClosedForBid) && (
              <Menus
                onClick={() => {
                  setDeleteJobId(rowInfo.jobId);
                  setDeleteJobName(rowInfo.jobName);
                  handleClickOpen();
                }}>
                <img src={Icons.DeleteIcon} alt={t('altTexts:delete')} /> {t('dashboard:delete')}
              </Menus>
            )}
          </MenuWrapper>
        )}
      </ActionsColumn>
    );
  };

  const columns = {
    jobName: {
      accessorKey: tabSelected === 2 ? 'name' : 'jobName',
      header: tabSelected === 1 ? t('tableHeader:otjJobDescription') : t('tableHeader:jobName'),
      enableSorting: true,
      state: { sortBy, columnFilters },
      enableColumnFilter: true,
      muiEditTextFieldProps: {
        required: true,
        type: 'number',
        variant: 'outlined',
      },
      size: 210,
      Cell: ({ renderedCellValue, row }: any) => (
        <div style={{ color: theme.palette.primary.dark }}>{renderJobLinkCell(renderedCellValue, row)}</div>
      ),
      enableColumnActions: false,
      enableHiding: false,
    },
    otjRequestedBy: {
      accessorKey: 'jobCreatorName',
      header: t('tableHeader:otjRequestedBy'),
      enableSorting: false,
      enableColumnFilter: false,
      size: 180,
      Cell: ({ renderedCellValue }: any) => renderCell(renderedCellValue),
      enableColumnActions: false,
      sortingFn: 'alphanumericCaseSensitive',
      enableHiding: false,
    },
    facility: {
      accessorKey: 'facilityName',
      header: t('tableHeader:facility'),
      enableSorting: true,
      enableColumnFilter: true,
      size: 234,
      Cell: ({ renderedCellValue }: any) => renderCell(renderedCellValue),
      enableColumnActions: false,
      sortingFn: 'alphanumericCaseSensitive',
    },
    city: {
      accessorKey: 'city',
      header: t('tableHeader:city'),
      enableSorting: false,
      enableColumnFilter: true,
      size: 180,
      Cell: ({ renderedCellValue }: any) => renderCell(renderedCellValue),
      enableColumnActions: false,
      sortingFn: 'alphanumericCaseSensitive',
      enableHiding: false,
    },
    state: {
      accessorKey: 'state',
      header: t('tableHeader:state'),
      enableSorting: false,
      enableColumnFilter: true,
      size: 180,
      Cell: ({ renderedCellValue }: any) => states[renderedCellValue as keyof typeof states] || renderedCellValue,
      enableColumnActions: false,
      sortingFn: 'alphanumericCaseSensitive',
      enableHiding: false,
    },
    startDate: {
      accessorKey: 'jobStartDate',
      header: t('calendar:startDate'),
      enableSorting: true,
      enableColumnFilter: false,
      size: 178,
      Cell: ({ cell }: any) => formatDateCell(cell),
      enableColumnActions: false,
      enableHiding: false,
    },
    endDate: {
      accessorKey: 'jobEndDate',
      header: t('calendar:endDate'),
      enableSorting: true,
      enableColumnFilter: false,
      size: 178,
      Cell: ({ cell }: any) => formatDateCell(cell),
      enableColumnActions: false,
      enableHiding: false,
    },
    services: {
      accessorKey: 'numberOfServices',
      header: t('tableHeader:noOfServices'),
      enableSorting: false,
      enableColumnFilter: false,
      size: 178,
      Cell: ({ renderedCellValue }: any) => renderCell(renderedCellValue),
      enableColumnActions: false,
    },
    status: {
      accessorKey: 'status',
      header: t('tableHeader:status'),
      enableSorting: true,
      enableColumnFilter: false,
      size: 142,
      Cell: ({ renderedCellValue }: any) => renderStatusCell(renderedCellValue),
    },
    actions: {
      header: t('tableHeader:actions'),
      enableColumnActions: false,
      size: 90,
      Cell: ({ row }: any) => renderActionsCell(row),
    },
  };

  const recurringJobColumns = useMemo<MRT_ColumnDef<IJobOverview | TaskData | IJobs | IBidList | IProfileCardProps>[]>(
    () => [
      {
        accessorKey: 'jobName',
        header: t('tableHeader:jobName'),
        enableSorting: true,
        state: { sortBy, columnFilters },
        enableColumnFilter: true,
        muiEditTextFieldProps: {
          required: true,
          type: 'number',
          variant: 'outlined',
        },
        size: 210,
        Cell: ({ renderedCellValue, row }: any) => (
          <div style={{ color: theme.palette.primary.dark }}>{renderJobLinkCell(renderedCellValue, row)}</div>
        ),
        enableColumnActions: false,
        enableHiding: false,
      },
      columns?.facility,
      columns?.city,
      columns?.state,
      columns?.startDate,
      columns?.endDate,
      columns?.services,
      columns?.status,
      {
        header: t('tableHeader:actions'),
        enableColumnActions: false,
        enablePinning: false,
        size: 90,
        Cell: ({ row }) => renderActionsCell(row),
      },
    ],
    [t, theme, handleClose]
  );

  const oneTimeJobColumns = useMemo<MRT_ColumnDef<IJobOverview | TaskData | IJobs | IBidList | IProfileCardProps>[]>(
    () => [
      {
        accessorKey: 'jobDescription',
        header: t('tableHeader:systemJobName'),
        enableSorting: true,
        Cell: ({ renderedCellValue }: any) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'jobName',
        header: t('tableHeader:otjJobDescription'),
        enableSorting: true,
        state: { sortBy, columnFilters },
        enableColumnFilter: true,
        muiEditTextFieldProps: {
          required: true,
          type: 'number',
          variant: 'outlined',
        },
        size: 210,
        Cell: ({ renderedCellValue, row }: any) => (
          <div style={{ color: theme.palette.primary.dark }}>{renderJobLinkCell(renderedCellValue, row)}</div>
        ),
        enableColumnActions: false,
        enableHiding: false,
      },
      columns?.otjRequestedBy,
      columns?.facility,
      columns?.city,
      columns?.state,
      columns?.startDate,
      columns?.endDate,
      columns?.services,
      columns?.status,
      {
        header: t('tableHeader:actions'),
        enableColumnActions: false,
        enablePinning: false,
        size: 90,
        Cell: ({ row }) => renderActionsCell(row),
      },
    ],
    [t, theme, handleClose]
  );

  const completedJobColumns = useMemo<MRT_ColumnDef<IJobOverview | TaskData | IJobs | IBidList | IProfileCardProps>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('tableHeader:jobName'),
        enableSorting: true,
        state: { sortBy, columnFilters },
        enableColumnFilter: true,
        muiEditTextFieldProps: {
          required: true,
          type: 'number',
          variant: 'outlined',
        },
        size: 210,
        Cell: ({ renderedCellValue, row }: any) => (
          <div style={{ color: theme.palette.primary.dark }}>{renderJobLinkCell(renderedCellValue, row)}</div>
        ),
        enableColumnActions: false,
        enableHiding: false,
      },
      columns?.facility,
      columns?.startDate,
      columns?.endDate,
      columns?.services,
      columns?.status,
      {
        header: t('tableHeader:actions'),
        enableColumnActions: false,
        enablePinning: false,
        size: 90,
        Cell: ({ row }) => renderActionsCell(row),
      },
    ],
    [t, theme, handleClose]
  );

  useEffect(() => {
    localStorage.removeItem('locationState');
  }, []);

  const processData = getProcessData();
  const canCreateOTJJob = processData?.some(
    (process: { processId: string | number }) => String(process?.processId) === USER_PROCESSES.OTJ
  );

  return (
    <>
      <PageTitle title={t('dashboard:jobs')} />
      <CustomerContainer>
        <TabSection sx={{ height: '100%' }}>
          <TabsWrapper>
            <Tabs value={tabSelected} onChange={handleChangeTabs} aria-label="basic tabs">
              <TabTitle label={t('dashboard:recurring')} {...a11yProps(0)} />
              <TabTitle label={t('dashboard:oneTime')} {...a11yProps(1)} />
              <TabTitle label={t('dashboard:completedJobs')} {...a11yProps(2)} />
            </Tabs>
          </TabsWrapper>
          <TabPanel value={tabSelected} index={0} style={{ height: '100%' }}>
            <DataGrid
              columns={recurringJobColumns}
              data={recurringJob}
              enableRowSelect={false}
              enableColumnPinning={false}
              loader={isLoading}
              sortingObj={sorting}
              sort={sortingJobs}
              sortingFns={{
                myCustomSortingFn: myCustomSortingFn,
              }}
              filter={filterOneTimeJobs}
              errorMessageTitle={noDataYet}
              errorMessageDescription={noDataComeBack}
            />
            {!isLoading && (
              <Box sx={{ display: 'flex', justifyContent: StyleConstants.JustifyContentBetween }}>
                {!isLoading && totalCount > 10 && (
                  <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                    <StyledPagination
                      count={Math.ceil(totalCount / itemsPerPage)}
                      page={currentPage}
                      disabled={isLoading}
                      onChange={(event, value) => {
                        handleChange(event, value);
                        if (newSortOrder) {
                          fetchJobsView('Recurring', value, itemsPerPage, newSortOrder, newSort);
                        } else {
                          fetchJobsView('Recurring', value, itemsPerPage, '', '');
                        }
                      }}
                      color="primary"
                    />
                  </Stack>
                )}
                {totalCount > 0 && (
                  <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                    <Typography>{`Total items: ${totalCount}`}</Typography>
                  </Stack>
                )}
                {totalCount > 10 && (
                  <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <Typography>{t('dashboard:recordsPerPage')}:</Typography>
                    <DropDown>
                      <Box onClick={toggleValue}>
                        <Select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          label={t('altTexts:records')}
                          input={<SelectInput />}
                          IconComponent={() => (
                            <img
                              src={Icons.DropdownIcon}
                              style={{
                                transform: isValueOpen ? Labels.Rotate : 'none',
                                position: 'absolute',
                                right: '0.75rem',
                              }}
                            />
                          )}
                          open={isValueOpen}
                          onClose={toggleValue}
                          onOpen={toggleValue}>
                          {NumberOfRowsOptions.map((dataItem) => (
                            <Options key={dataItem.id} value={dataItem.value}>
                              {dataItem.label}
                            </Options>
                          ))}
                        </Select>
                      </Box>
                    </DropDown>
                  </Box>
                )}
              </Box>
            )}
          </TabPanel>
          <TabPanel value={tabSelected} index={1} style={{ height: '100%' }}>
            <DataGrid
              columns={oneTimeJobColumns}
              data={oneTimeJobs}
              enableRowSelect={false}
              enableColumnPinning={false}
              loader={isLoading}
              sortingObj={sorting}
              sort={sortingJobs}
              filter={filterOneTimeJobs}
              sortingFns={{
                myCustomSortingFn: myCustomSortingFn,
              }}
              errorMessageTitle={noDataYet}
              errorMessageDescription={noDataComeBack}
            />
            {!isLoading && (
              <Box sx={{ display: 'flex', justifyContent: StyleConstants.JustifyContentBetween }}>
                {!isLoading && totalCount > 10 && (
                  <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                    <StyledPagination
                      count={Math.ceil(totalCount / itemsPerPage)}
                      page={currentPage}
                      disabled={isLoading}
                      onChange={(event, value) => {
                        handleChange(event, value);
                        if (newSortOrder) {
                          fetchJobsView('OneTime', value, itemsPerPage, newSortOrder, newSort);
                        } else {
                          fetchJobsView('OneTime', value, itemsPerPage, '', '');
                        }
                      }}
                      color="primary"
                    />
                  </Stack>
                )}
                {totalCount > 0 && (
                  <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                    <Typography>{`Total items: ${totalCount}`}</Typography>
                  </Stack>
                )}
                {totalCount > 10 && (
                  <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <Typography>{t('dashboard:recordsPerPage')}:</Typography>
                    <DropDown>
                      <Box onClick={toggleValue}>
                        <Select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          label={t('altTexts:records')}
                          input={<SelectInput />}
                          IconComponent={() => (
                            <img
                              src={Icons.DropdownIcon}
                              style={{
                                transform: isValueOpen ? Labels.Rotate : 'none',
                                position: 'absolute',
                                right: '0.75rem',
                              }}
                            />
                          )}
                          open={isValueOpen}
                          onClose={toggleValue}
                          onOpen={toggleValue}>
                          {NumberOfRowsOptions.map((dataItem) => (
                            <Options key={dataItem.id} value={dataItem.value}>
                              {dataItem.label}
                            </Options>
                          ))}
                        </Select>
                      </Box>
                    </DropDown>
                  </Box>
                )}
              </Box>
            )}
          </TabPanel>
          <TabPanel value={tabSelected} index={2} style={{ height: '100%' }}>
            <DataGrid
              columns={completedJobColumns}
              data={completedJobs}
              enableRowSelect={false}
              enableColumnPinning={false}
              loader={isLoading}
              sortingObj={sorting}
              filter={filterJobs}
              sort={sortingJobs}
              sortingFns={{
                myCustomSortingFn: myCustomSortingFn,
              }}
              errorMessageTitle={noDataYet}
              errorMessageDescription={noDataComeBack}
            />
            {!isLoading && (
              <Box sx={{ display: 'flex', justifyContent: StyleConstants.JustifyContentBetween }}>
                {!isLoading && totalCount > 10 && (
                  <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                    <StyledPagination
                      count={Math.ceil(totalCount / itemsPerPage)}
                      page={currentPage}
                      disabled={isLoading}
                      onChange={(event, value) => {
                        handleChange(event, value);
                        if (newSortOrder || columnFilters) {
                          fetchCompletedJobs(value, newSortOrder, newSort, columnFilters);
                        } else {
                          fetchCompletedJobs(value, '', '', []);
                        }
                      }}
                    />
                  </Stack>
                )}
                {totalCount > 0 && (
                  <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                    <Typography>{`Total items: ${totalCount}`}</Typography>
                  </Stack>
                )}
              </Box>
            )}
          </TabPanel>
        </TabSection>
        <SnackBarWrapper>
          {tabSelected === 1 && canCreateOTJJob && (
            <Link to="/createOneTimeJob">
              <Snackbar
                open
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                action={
                  <Button
                    icon={<img src={Icons.PlusWhiteIcon} alt={t('altTexts:addIcon')} />}
                    type={ButtonType.Primary}
                    label={t('homePage:oneTimeJob')}
                  />
                }
              />
            </Link>
          )}
        </SnackBarWrapper>
        <SnackbarMessage
          open={snackbarOpen}
          successMessage={snackbarMessage}
          errorMessage={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
        <DeleteJobs
          isApproveWindowOpen={dialogueBoxOpen}
          handleCloseApproveWindow={() => setDialogueBoxOpen(false)}
          deleteJobId={deleteJobId}
          deleteJobName={deleteJobName}
          fetchJobsView={fetchJobsView}
          jobType={jobType}
          fetchData={() => {}}></DeleteJobs>
      </CustomerContainer>
    </>
  );
};

export default JobsView;
